<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue';
import TooltipDialog from '../dialogs/TooltipDialog.vue';
import InfoIcon from '@/assets/icons/InfoIcon.vue';
import { EPosition } from 'common/enum/misc.enum';

const EDGE_ALIGNED_OFFSET_X = 20;
const HOVER_OPEN_DELAY_MS = 500;
const HOVER_CLOSE_DELAY_MS = 120;

type InfoTooltipTrigger = 'click' | 'hover' | 'click-and-hover';

const props = withDefaults(
  defineProps<{
    position?: EPosition;
    theme?: 'tint' | 'info';
    accessibleLabel?: string;
    tooltipText?: string;
    contentStyle?: string;
    popoverContentStyle?: string;
    iconStyle?: string;
    buttonStyle?: string;
    offsetX?: number | null;
    showArrow?: boolean;
    trigger?: InfoTooltipTrigger;
  }>(),
  {
    buttonStyle: 'h-icon w-icon',
    theme: 'tint',
    accessibleLabel: undefined,
    position: EPosition.BOTTOM_CENTER,
    tooltipText: '',
    contentStyle: '',
    popoverContentStyle: '',
    iconStyle: 'w-icon-xs h-icon-xs',
    offsetX: null,
    showArrow: true,
    trigger: 'click',
  },
);

const isDialogOpen = ref<boolean>(false);
const isPinned = ref<boolean>(false);
const isOverAnchor = ref<boolean>(false);
const isOverDialog = ref<boolean>(false);
const anchorElement = useTemplateRef<HTMLElement>('anchorElement');
const dialogRef = useTemplateRef<InstanceType<typeof TooltipDialog>>('dialogRef');

let hoverTimer: number | null = null;
let closeTimer: number | null = null;

const canOpenOnClick = computed<boolean>(() => props.trigger === 'click' || props.trigger === 'click-and-hover');
const canOpenOnHover = computed<boolean>(() => props.trigger === 'hover' || props.trigger === 'click-and-hover');

const resolvedOffsetX = computed<number>(() => {
  if (props.offsetX != null) return props.offsetX;
  if (props.position === EPosition.BOTTOM_LEFT) return EDGE_ALIGNED_OFFSET_X;
  if (props.position === EPosition.BOTTOM_RIGHT) return -EDGE_ALIGNED_OFFSET_X;
  return 0;
});

const clearHoverTimer = (): void => {
  if (!hoverTimer) return;
  window.clearTimeout(hoverTimer);
  hoverTimer = null;
};

const clearCloseTimer = (): void => {
  if (!closeTimer) return;
  window.clearTimeout(closeTimer);
  closeTimer = null;
};

const handleShowDialog = (): void => {
  clearCloseTimer();
  isDialogOpen.value = true;
  dialogRef.value?.show();
};

const handleHideDialog = (): void => {
  clearHoverTimer();
  clearCloseTimer();
  isPinned.value = false;
  isOverAnchor.value = false;
  isOverDialog.value = false;
  isDialogOpen.value = false;
  dialogRef.value?.hide();
};

const startHoverTimer = (): void => {
  if (!canOpenOnHover.value || isDialogOpen.value) return;
  clearHoverTimer();
  hoverTimer = window.setTimeout(() => {
    hoverTimer = null;
    if (!isOverAnchor.value) return;
    handleShowDialog();
  }, HOVER_OPEN_DELAY_MS);
};

const scheduleCloseIfIdle = (): void => {
  if (!canOpenOnHover.value || isPinned.value) return;
  clearCloseTimer();
  closeTimer = window.setTimeout(() => {
    closeTimer = null;
    if (!isOverAnchor.value && !isOverDialog.value) {
      handleHideDialog();
    }
  }, HOVER_CLOSE_DELAY_MS);
};

const handleTriggerClick = (): void => {
  if (!canOpenOnClick.value) return;
  clearHoverTimer();
  clearCloseTimer();

  if (isDialogOpen.value && isPinned.value) {
    handleHideDialog();
    return;
  }

  isPinned.value = true;
  handleShowDialog();
};

const handleAnchorMouseEnter = (): void => {
  if (!canOpenOnHover.value) return;
  isOverAnchor.value = true;
  clearCloseTimer();
  startHoverTimer();
};

const handleAnchorMouseLeave = (): void => {
  if (!canOpenOnHover.value) return;
  isOverAnchor.value = false;
  clearHoverTimer();
  scheduleCloseIfIdle();
};

const handleContentMouseEnter = (): void => {
  if (!canOpenOnHover.value) return;
  isOverDialog.value = true;
  clearCloseTimer();
};

const handleContentMouseLeave = (): void => {
  if (!canOpenOnHover.value) return;
  isOverDialog.value = false;
  scheduleCloseIfIdle();
};

const handleDialogHide = (): void => {
  clearHoverTimer();
  clearCloseTimer();
  isPinned.value = false;
  isOverDialog.value = false;
  isDialogOpen.value = false;
};

onBeforeUnmount(() => {
  clearHoverTimer();
  clearCloseTimer();
});

defineExpose({ hide: handleHideDialog, show: handleShowDialog });
</script>

<template>
  <button
    ref="anchorElement"
    :aria-label="accessibleLabel"
    :class="[
      'flex items-center justify-center',
      'rounded-full',
      buttonStyle,
      'transition duration-300',
      'active:text-tint-hover focus-visible:outline-focus',
      'hover:text-tint-hover',
      [isDialogOpen ? 'text-tint' : 'text-secondary'],
    ]"
    type="button"
    @click.stop="handleTriggerClick"
    @focus="handleAnchorMouseEnter"
    @blur="handleAnchorMouseLeave"
    @mouseenter="handleAnchorMouseEnter"
    @mouseleave="handleAnchorMouseLeave"
  >
    <InfoIcon :class="['shrink-0', iconStyle]" />
  </button>
  <TooltipDialog
    v-if="anchorElement"
    ref="dialogRef"
    :anchor-element="anchorElement"
    :position="props.position"
    :theme="props.theme"
    :content-style="props.contentStyle"
    :popover-content-style="props.popoverContentStyle"
    :offset-x="resolvedOffsetX"
    :show-arrow="props.showArrow"
    @hide="handleDialogHide"
    @mouseenter:content="handleContentMouseEnter"
    @mouseleave:content="handleContentMouseLeave"
  >
    <template #content>
      <slot>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="props.tooltipText"></span>
      </slot>
    </template>
  </TooltipDialog>
</template>
